import { FC, useEffect, useRef } from 'react'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import {
  CookieCategory,
  useOneTrustConsentCategory,
} from '@moonpig/web-core-browser-storage'
import { useSessionId } from '@moonpig/web-core-utils'
import { useLoggedIn } from '@moonpig/web-core-auth'
import { logger } from '@moonpig/web-core-monitoring'
import { identifyUser, initMixpanel } from './mixpanelClient'
import { useGetCustomerId } from './useGetCustomerId'

export const MixpanelSetup: FC<{ url: string }> = ({ url }) => {
  const isInitialised = useRef(false)
  const { sessionId } = useSessionId()
  const { loggedIn } = useLoggedIn()
  const { getCustomerId } = useGetCustomerId()

  const mixpanelRecordingPercent = useExperiment('mixpanel-recording-percent', {
    fallback: 0,
  })

  const mixpanelEnabled = useExperiment('mixpanel-enabled', {
    fallback: false,
  })

  const mixpanelMaskAll = useExperiment('mixpanel-mask-all', {
    fallback: false,
  })

  const userHasConsented = useOneTrustConsentCategory(
    CookieCategory.PERFORMANCE,
  )

  const shouldIdentify =
    loggedIn && isInitialised.current && userHasConsented && mixpanelEnabled

  // Initialize Mixpanel when consent given and enabled
  useEffect(() => {
    if (isInitialised.current) return

    if (userHasConsented && mixpanelEnabled) {
      isInitialised.current = true
      const initialiseMixpanel = async () => {
        try {
          await initMixpanel(
            mixpanelRecordingPercent,
            sessionId,
            mixpanelMaskAll,
          )
        } catch (error) {
          logger.warning('Could not initialize Mixpanel', { url }, error)
        }
      }

      initialiseMixpanel()
    }
  }, [
    url,
    userHasConsented,
    mixpanelEnabled,
    mixpanelRecordingPercent,
    mixpanelMaskAll,
    sessionId,
  ])

  // Identify user in Mixpanel when logged in, consent given, and Mixpanel enabled
  useEffect(() => {
    if (!shouldIdentify) return

    const identifyCurrentUser = async () => {
      try {
        const { data } = await getCustomerId()
        if (data?.me?.customer?.id) {
          await identifyUser(data.me.customer.id)
        }
      } catch (error) {
        logger.warning('Could not identify user in Mixpanel', {}, error)
      }
    }

    identifyCurrentUser()
  }, [getCustomerId, shouldIdentify])

  return null
}
